import { union } from 'lodash';
import { all, put, select, takeLatest } from 'typed-redux-saga/macro';

import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';
import {
  fetchMediaCommentsUser,
  selectCommentUsers,
  selectIsBasicUserQuerySupported,
} from '@common-modules/mediaDetails';

import {
  FETCH_DOCUMENT_COMMENTS,
  FETCH_DOCUMENT_COMMENTS_SUCCESS,
  LOG_AUDIT_EVENT,
  UPDATE_DOCUMENT_COMMENT,
  UPDATE_DOCUMENT_COMMENT_FAILURE,
  UPDATE_DOCUMENT_COMMENT_SUCCESS,
} from '../actions';
import * as Services from '../services';

/**
 * Document-comments saga (VE-21982).
 *
 * Parallels common-modules/mediaDetails/saga/mediaComments but routes
 * fetch / update through the document-comments service thunks. User
 * lookups continue to ride on the shared mediaDetails pipeline — users
 * are app-wide and there's no value in maintaining a parallel cache.
 */

function* fetchDocumentComments() {
  yield* takeLatest(FETCH_DOCUMENT_COMMENTS, function* ({ payload }) {
    yield* put(Services.fetchDocumentComments(payload));
  });
}

function* fetchDocumentCommentsSuccess() {
  yield* takeLatest(FETCH_DOCUMENT_COMMENTS_SUCCESS, function* ({ payload }) {
    const {
      structuredDataObjects: { records },
    } = payload;
    // Hydrate user info for any author we haven't seen yet. We reuse the
    // mediaDetails user cache + action so a user already known from the
    // video flow doesn't get re-fetched here, and vice versa.
    const users = yield* select(selectCommentUsers);
    const isBasicUserQuerySupported = yield* select(
      selectIsBasicUserQuerySupported
    );
    if (!isBasicUserQuerySupported) {
      return;
    }
    const userIds = records.reduce<string[]>(
      (prev, { data: { createdBy } }) => union(prev, [createdBy]),
      []
    );
    yield* all(
      userIds.map(
        (userId: string) =>
          !users[userId] && put(fetchMediaCommentsUser(userId))
      )
    );
  });
}

/**
 * Comment activity is intentionally kept OUT of the audit log for now
 * (VE-20840 review). The emit code is preserved but gated behind this flag so
 * it can be turned back on without re-deriving the message shape.
 */
const AUDIT_LOG_COMMENTS = false;

function* updateDocumentComment() {
  yield* takeLatest(UPDATE_DOCUMENT_COMMENT, function* ({ payload }) {
    const { comment, modify, isUndo } = payload;
    yield* put(Services.updateDocumentComment({ comment, modify, isUndo }));
    if (AUDIT_LOG_COMMENTS) {
      yield* put(
        LOG_AUDIT_EVENT({
          action: `${
            !comment.done
              ? 'New Document Comment'
              : comment.archived
                ? 'Hide Document Comment'
                : 'Update Document Comment'
          } ${JSON.stringify(comment)}`,
        })
      );
    }
  });
}

function* updateDocumentCommentSuccess() {
  yield* takeLatest(UPDATE_DOCUMENT_COMMENT_SUCCESS, function* () {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'successfullySavedComment',
          defaultMessage: 'Successfully saved comment.',
        }),
        variant: 'success',
      })
    );
  });
}

function* updateDocumentCommentFailure() {
  yield* takeLatest(UPDATE_DOCUMENT_COMMENT_FAILURE, function* () {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'failedToSaveComment',
          defaultMessage: 'Failed to save comment.',
        }),
        variant: 'error',
      })
    );
  });
}

export function* documentCommentsSaga() {
  yield* all([
    fetchDocumentComments(),
    fetchDocumentCommentsSuccess(),
    updateDocumentComment(),
    updateDocumentCommentSuccess(),
    updateDocumentCommentFailure(),
  ]);
}
